import { ReactNode, useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment,
  Assignment,
  BugReport,
  Calculate,
  Dashboard,
  LocalShipping,
  ManageAccounts,
  SupervisorAccount,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentUserProfile } from '~/hooks/useCurrentUserProfile';
import {
  buildPermissionSet,
  CN01_MANAGE_ACCESS_PERMISSIONS,
  CN01_PLANTER_DASHBOARD_PERMISSIONS,
  CN02_FORM_ACCESS_PERMISSIONS,
  CN03_FORM_ACCESS_PERMISSIONS,
  CN03_RG_MANAGE_ACCESS_PERMISSIONS,
  CN04_MANAGE_ACCESS_PERMISSIONS,
  CN04_FORM_CREATE_PERMISSIONS,
  CN05_MANAGE_ACCESS_PERMISSIONS,
  CN05_FORM_CREATE_PERMISSIONS,
  CN06_MANAGE_ACCESS_PERMISSIONS,
  CN06_FORM_CREATE_PERMISSIONS,
  hasAnyPermission,
  resolveCn03ManagePath,
  resolveCn04ManagePath,
  resolveCn05ManagePath,
  resolveCn06ManagePath,
  FINANCIAL_MANAGE_ACCESS_PERMISSIONS,
  PEST_DISEASE_MANAGE_ACCESS_PERMISSIONS,
  REPORTS_MODULE_ACCESS_PERMISSIONS,
  ROLES_MODULE_ACCESS_PERMISSIONS,
  SUPPLIER_MODULE_ACCESS_PERMISSIONS,
  USERS_MODULE_ACCESS_PERMISSIONS,
} from '~/lib/permissions';
import { APP_DISPLAY_VERSION, ROUTES } from '~/utils/constants';

export const SIDEBAR_WIDTH_EXPANDED = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

type MenuItem = {
  text: string;
  icon: ReactNode;
  path: string;
  /** If set, user must have at least one of these permissions. */
  requiredAny?: readonly string[];
};

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/app/dashboard' },
  {
    text: 'CN01',
    icon: <Assignment />,
    path: ROUTES.CN01_MANAGE,
    requiredAny: CN01_MANAGE_ACCESS_PERMISSIONS,
  },
  {
    text: 'CN02',
    icon: <Assignment />,
    path: ROUTES.CN02_MANAGE,
    requiredAny: CN02_FORM_ACCESS_PERMISSIONS,
  },
  {
    text: 'CN03',
    icon: <Assignment />,
    path: ROUTES.CN03_MANAGE,
    requiredAny: [...CN03_FORM_ACCESS_PERMISSIONS, ...CN03_RG_MANAGE_ACCESS_PERMISSIONS],
  },
  {
    text: 'CN04',
    icon: <Assignment />,
    path: ROUTES.CN04_MANAGE,
    requiredAny: [...CN04_MANAGE_ACCESS_PERMISSIONS, ...CN04_FORM_CREATE_PERMISSIONS],
  },
  {
    text: 'CN05',
    icon: <Assignment />,
    path: ROUTES.CN05_MANAGE,
    requiredAny: [...CN05_MANAGE_ACCESS_PERMISSIONS, ...CN05_FORM_CREATE_PERMISSIONS],
  },
  {
    text: 'CN06',
    icon: <Assignment />,
    path: ROUTES.CN06_MANAGE,
    requiredAny: [...CN06_MANAGE_ACCESS_PERMISSIONS, ...CN06_FORM_CREATE_PERMISSIONS],
  },
  {
    text: 'Reports',
    icon: <Assessment />,
    path: ROUTES.REPORTS,
    requiredAny: REPORTS_MODULE_ACCESS_PERMISSIONS,
  },
  {
    text: 'Suppliers',
    icon: <LocalShipping />,
    path: ROUTES.SUPPLIERS_MANAGE,
    requiredAny: SUPPLIER_MODULE_ACCESS_PERMISSIONS,
  },
  {
    text: 'Pest & Disease',
    icon: <BugReport />,
    path: ROUTES.PEST_DISEASE_MANAGE,
    requiredAny: PEST_DISEASE_MANAGE_ACCESS_PERMISSIONS,
  },
  {
    text: 'Financial',
    icon: <Calculate />,
    path: ROUTES.FINANCIAL_MANAGE,
    requiredAny: FINANCIAL_MANAGE_ACCESS_PERMISSIONS,
  },
  {
    text: 'Users',
    icon: <ManageAccounts />,
    path: ROUTES.USERS_MANAGE,
    requiredAny: USERS_MODULE_ACCESS_PERMISSIONS,
  },
  {
    text: 'Roles',
    icon: <SupervisorAccount />,
    path: ROUTES.ROLES_MANAGE,
    requiredAny: ROLES_MODULE_ACCESS_PERMISSIONS,
  },
];

export function Sidebar({ open }: { open: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useCurrentUserProfile();
  const permissionSet = useMemo(() => buildPermissionSet(data?.permissions), [data?.permissions]);
  const isPlanter = useMemo(
    () => hasAnyPermission(permissionSet, CN01_PLANTER_DASHBOARD_PERMISSIONS),
    [permissionSet]
  );
  const visibleItems = useMemo(
    () =>
      menuItems.filter((item) => {
        if (!item.requiredAny?.length) return true;
        return hasAnyPermission(permissionSet, item.requiredAny);
      }),
    [permissionSet]
  );

  const drawerWidth = open ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  if (isPlanter) {
    return null;
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          marginTop: '64px',
          height: 'calc(100% - 64px)',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'hidden',
          borderRight: (theme) => `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.dark, 0.12)
              : alpha(theme.palette.common.black, 0.2),
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <Box
        sx={{
          px: open ? 2 : 1,
          py: open ? 2 : 1.5,
          flexShrink: 0,
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
        }}
      >
        {open ? (
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            Version {APP_DISPLAY_VERSION}
          </Typography>
        ) : (
          <Tooltip title={`Version ${APP_DISPLAY_VERSION}`} placement="right">
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'center',
                fontSize: '0.6rem',
                lineHeight: 1.25,
                whiteSpace: 'normal',
              }}
            >
              Version
              <br />
              {APP_DISPLAY_VERSION}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <List
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 0,
        }}
      >
        {visibleItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={open ? '' : item.text} placement="right" disableHoverListener={open}>
              <ListItemButton
                selected={
                  item.path === ROUTES.CN02_MANAGE
                    ? location.pathname.startsWith('/app/cn02')
                    : item.path === ROUTES.CN01_MANAGE
                      ? location.pathname.startsWith('/app/cn01')
                      : item.path === ROUTES.CN03_MANAGE
                        ? location.pathname.startsWith('/app/cn03')
                        : item.path === ROUTES.CN04_MANAGE
                          ? location.pathname.startsWith('/app/cn04')
                          : item.path === ROUTES.CN05_MANAGE
                            ? location.pathname.startsWith('/app/cn05')
                            : item.path === ROUTES.CN06_MANAGE
                              ? location.pathname.startsWith('/app/cn06')
                              : item.path === ROUTES.USERS_MANAGE
                              ? location.pathname.startsWith('/app/users')
                              : item.path === ROUTES.ROLES_MANAGE
                                ? location.pathname.startsWith('/app/roles')
                                : item.path === ROUTES.PEST_DISEASE_MANAGE
                                  ? location.pathname.startsWith('/app/pest-disease-manage')
                                  : item.path === ROUTES.FINANCIAL_MANAGE
                                    ? location.pathname.startsWith('/app/financial-manage') ||
                                      location.pathname.startsWith('/app/financial/')
                                    : location.pathname === item.path
                }
                onClick={() => {
                  if (item.path === ROUTES.CN04_MANAGE) {
                    navigate(resolveCn04ManagePath(permissionSet));
                    return;
                  }
                  if (item.path === ROUTES.CN05_MANAGE) {
                    navigate(resolveCn05ManagePath(permissionSet));
                    return;
                  }
                  if (item.path === ROUTES.CN06_MANAGE) {
                    navigate(resolveCn06ManagePath(permissionSet));
                    return;
                  }
                  if (item.path === ROUTES.CN03_MANAGE) {
                    navigate(resolveCn03ManagePath(permissionSet));
                    return;
                  }
                  navigate(item.path);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '& .MuiListItemIcon-root': {
                    color: 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                  },
                  '&:hover:not(.Mui-selected) .MuiListItemIcon-root': {
                    color: 'primary.dark',
                  },
                  '&.Mui-selected': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.26),
                    color: 'primary.dark',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.32),
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.dark',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}
